import React, { useState } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import { Card, Chip, Divider, Link, Modal, Row, Stack, Text } from 'library/ui';
import { Colors } from 'library';

import {
    AllowanceBar,
    RemainingLabel,
    UsageBreakdown,
    isCloseToLimit,
    isOutOfShifts,
    type FeeFreeShiftAllowance,
} from 'parent-portal/shared/FeeFreeShifts/allowance';

function SummaryContent({ allowances }: { allowances: FeeFreeShiftAllowance[] }) {
    if (allowances.length === 1) {
        const allowance = allowances[0];
        return (
            <Row gap="md" align="center" wrap>
                <AllowanceBar allowance={allowance} compact />
                <Row gap="sm" align="center">
                    <RemainingLabel allowance={allowance} />
                    <Text size="sm" tone="secondary">
                        this month
                    </Text>
                </Row>
            </Row>
        );
    }

    if (allowances.length === 2) {
        return (
            <Row gap="lg" align="center" wrap>
                {allowances.map((allowance) => (
                    <Row key={allowance.business_location_id} gap="sm" align="center">
                        <Text size="sm" tone="secondary">
                            {allowance.business_location_name}
                        </Text>
                        <RemainingLabel allowance={allowance} />
                    </Row>
                ))}
            </Row>
        );
    }

    const withShiftsLeft = allowances.filter((allowance) => !isOutOfShifts(allowance)).length;
    const closeToLimit = allowances.filter(isCloseToLimit).length;

    return (
        <Row gap="md" align="center" wrap>
            <Text size="sm">
                {withShiftsLeft} of {allowances.length} locations have shifts left
            </Text>
            {closeToLimit > 0 && (
                <Chip size="small" bgColor={Colors.warningBackground} textColor={Colors.warning}>
                    {closeToLimit} close to limit
                </Chip>
            )}
        </Row>
    );
}

function FeeFreeShiftsModal({
    allowances,
    poolName,
    onClose,
}: {
    allowances: FeeFreeShiftAllowance[];
    poolName: string | null;
    onClose: () => void;
}) {
    const { limit, resets_on: resetsOn } = allowances[0];
    const owner = poolName ? `${poolName}'s Substitute Pool` : 'Your substitute pool';

    return (
        <Modal onClose={onClose} width={560}>
            <Stack gap="md">
                <Stack gap="sm">
                    <Text size="lg" weight="bold">
                        Fee-Free Shifts — {format(subDays(parseISO(resetsOn), 1), 'MMMM')}
                    </Text>
                    <Text size="sm" tone="secondary" lineHeight="relaxed">
                        {owner} covers Tandem&apos;s administration fee on up to {limit} shifts per location each month.
                        Resets {format(parseISO(resetsOn), 'MMM d')}.
                    </Text>
                </Stack>
                {allowances.map((allowance) => (
                    <Stack key={allowance.business_location_id} gap="sm">
                        <Divider />
                        <Row justify="between" align="center" gap="md">
                            <Text weight="bold">{allowance.business_location_name}</Text>
                            <RemainingLabel allowance={allowance} />
                        </Row>
                        <AllowanceBar allowance={allowance} />
                        <UsageBreakdown allowance={allowance} />
                    </Stack>
                ))}
                <Divider />
                <Text size="sm" tone="secondary" lineHeight="relaxed">
                    Shifts count against the allowance when they&apos;re paid out. Scheduled shifts are staffed but not
                    paid yet — if one cancels, its fee-free shift frees up.
                </Text>
            </Stack>
        </Modal>
    );
}

export function FeeFreeShiftsCard({
    allowances,
    poolName,
}: {
    allowances: FeeFreeShiftAllowance[];
    poolName: string | null;
}) {
    const [detailsOpen, setDetailsOpen] = useState(false);

    if (allowances.length === 0) {
        return null;
    }

    return (
        <Card padding="md">
            <Row gap="md" align="center" justify="between" fullWidth wrap>
                <Row gap="md" align="center" wrap>
                    <Text weight="bold">Fee-free shifts</Text>
                    <SummaryContent allowances={allowances} />
                </Row>
                <Link size="sm" onClick={() => setDetailsOpen(true)}>
                    Details
                </Link>
            </Row>
            {detailsOpen && (
                <FeeFreeShiftsModal allowances={allowances} poolName={poolName} onClose={() => setDetailsOpen(false)} />
            )}
        </Card>
    );
}
